import React, { useMemo, useRef, useEffect } from 'react';
import classNames from 'classnames';
import ReactECharts from 'echarts-for-react';
import dataFormat from '@home/utils/dbDataFormat/dataFormat';
import * as echarts from 'echarts';
import './index.scss';

interface ProgressBarProps {
  className?: string;
  // 当前进度值
  value: number;
  // 预测值
  predictValue: number;
  // 新增预测值
  forecastValue?: number;
  // 目标值
  targetValue: number;
  // 进度条颜色
  barColor?: string;
  // 预测值的进度条颜色
  predictBarColor?: string;
  // 新增预测值的进度条颜色
  forecastBarColor?: string;
  // 进度条的背景色
  backgroundColor?: string;
  // 目标值标记的颜色
  markerColor?: string;
  // 是否显示进度值的文本
  showLabel?: boolean;
  // 是否显示目标值标记
  showMarker?: boolean;
  // 进度条高度
  height?: number;
  // 进度百分比保留的小数位数
  decimalPlaces?: number;
  // 数值格式化规则
  formattedValue?: string;
  // 刻度段数
  ticksNumber?: number;
  // 最大值扩展比例
  maxValueIncreaseRate?: number;
}

const ProgressBar = (props: ProgressBarProps) => {
  const {
    className,
    value,
    predictValue,
    forecastValue,
    targetValue,
    barColor = '#1E88E5',
    predictBarColor = '#64B5F6',
    forecastBarColor = '#B8BED1',
    backgroundColor = '#90CAF9',
    markerColor = '#4CAF50',
    showLabel = true,
    showMarker = true,
    height = 44,
    decimalPlaces = 2,
    formattedValue = 'bmk00',
    ticksNumber = 5,
    maxValueIncreaseRate = 1.3,
  } = props;

  const chartRef = useRef<echarts.ECharts | null>(null);

  // 计算进度条的最大刻度值
  const calculatedMaxValue = useMemo(() => {
    const highestValue = Math.max(value, predictValue, forecastValue || 0, targetValue || 0);
    return highestValue * maxValueIncreaseRate;
  }, [value, predictValue, forecastValue, targetValue, maxValueIncreaseRate]);

  // ECharts 刻度配置
  const eChartOption = useMemo(() => ({
    grid: {
      left: "3px",
      right: "15px",
      top: 0,
      bottom: 0,
      containLabel: true,
    },
    xAxis: {
      type: 'value',
      min: 0,
      max: calculatedMaxValue,
      splitNumber: ticksNumber,
      axisLine: { show: false },
      axisTick: {
        show: true,
        alignWithLabel: true,
        length: 5, // 默认tick长度
        lineStyle: {
          color: '#BBBDCB'
        },
        inside: true
      },
      axisLabel: {
        color: '#BBBDCB',
        fontSize: 12,
        formatter: (val: number) => dataFormat({ value: val, formatRule: 'bmk' }),
      },
      splitLine: {
        show: false
      }  
    },
    yAxis: { show: false },
    series: [],
  }), [calculatedMaxValue, ticksNumber]);

  // 计算进度条宽度的百分比
  const calculatePercentage = (val: number) => {
    const percentage = (val / calculatedMaxValue) * 100;
    return Math.min(Math.max(percentage, 0), 100);
  };

  // 计算当前值、预测值和目标值的百分比
  const valuePercentage = calculatePercentage(value);
  const predictValuePercentage = calculatePercentage(predictValue);
  const forecastValuePercentage = calculatePercentage(forecastValue || 0);
  const markerPercentage = calculatePercentage(targetValue);
  // 占比计算
  const ratio = value / targetValue;

  useEffect(() => {
    if (chartRef.current) {
      chartRef.current.setOption(eChartOption);
    }
  }, [eChartOption]);

  return (
    <div className={classNames('dbh-progress-bar-container', className)}>
      <div
        className="dbh-progress-bar-chart"
        style={{ height: `${height}px`, width: `calc(100% - 18px)`,left: "3px" }}
      >
        {/* 进度条背景 */}
        <div className="dbh-progress-bar-background" style={{ backgroundColor }} />
        {/* 预测值进度条 */}
        <div
          className="dbh-progress-bar-predict-value"
          style={{
            width: `${predictValuePercentage}%`,
            backgroundColor: predictBarColor,
          }}
        />
        {/* 新增预测值进度条 */}
        <div
          className="dbh-progress-bar-predict-value"
          style={{
            width: `${forecastValuePercentage}%`,
            backgroundColor: forecastBarColor,
          }}
        />

        {/* 当前值进度条 */}
        <div
          className="dbh-progress-bar-value"
          style={{
            width: `${valuePercentage}%`,
            backgroundColor: barColor,
            height: `${(height + 4) / 2}px`, // 让进度条稍微厚一点
            top: `${(height - 4) / 4}px`, // 居中显示
          }}
        >
          {/* 显示进度值标签 */}
          {showLabel && value && (
            <div className="dbh-progress-bar-value-label">
              {dataFormat({ value, formatRule: formattedValue })}
              {isFinite(ratio) && !isNaN(ratio) 
                ? ` (${(ratio * 100).toFixed(decimalPlaces)}%)`
                : ` (-)`}
            </div>
          )}
        </div>

        {/* 目标值标记 */}
        {showMarker && targetValue && (
          <div
            className="dbh-progress-bar-marker"
            style={{
              backgroundColor: markerColor,
              left: `${markerPercentage}%`,
            }}
          />
        )}
      </div>

      {/* ECharts 渲染刻度 */}
      <div className="dbh-progress-bar-ticks">
        <ReactECharts
          ref={(e) => {
            if (e) chartRef.current = e.getEchartsInstance();
          }}
          option={eChartOption}
          style={{ width: '100%', height: '30px' }}
        />
      </div>
    </div>
  );
};

export default ProgressBar;
